import { useState } from 'react';
import { useInventory } from './hooks/useInventory';
import { Vendor } from './types';

export default function VendorList() {
  const { vendors, addVendor } = useInventory();
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newVendor, setNewVendor] = useState<Partial<Vendor>>({
    name: '',
    contact: '',
    email: '',
    category: '',
    gstin: '',
    rating: 0
  });

  const handleAddVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const vendorData = {
        ...newVendor,
        id: crypto.randomUUID()
      };
      await addVendor.mutateAsync(vendorData);
      setShowAddForm(false);
      setNewVendor({
        name: '',
        contact: '',
        email: '',
        category: '',
        gstin: '',
        rating: 0
      });
    } catch (error) {
      console.error('Failed to add vendor:', error);
    }
  };

  const filteredVendors = vendors.data?.filter((vendor: Vendor) =>
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.category.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (vendors.isLoading) return <div className="p-6">Loading vendors...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-primary">Vendor Management</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line mr-2"></i>
          Add Vendor
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <input
          type="text"
          placeholder="Search vendors by name or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {/* Vendor Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVendors.map((vendor: Vendor) => (
          <div key={vendor.id} className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-primary">{vendor.name}</h3>
                <p className="text-sm text-gray-600">{vendor.category}</p>
              </div>
              {vendor.rating && (
                <div className="flex items-center">
                  <i className="ri-star-fill text-yellow-400 mr-1"></i>
                  <span className="text-sm font-medium">{vendor.rating}</span>
                </div>
              )}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <i className="ri-phone-line w-4 h-4 text-gray-400 mr-2"></i>
                <span>{vendor.contact}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-mail-line w-4 h-4 text-gray-400 mr-2"></i>
                <span className="truncate">{vendor.email}</span>
              </div>
              {vendor.gstin && (
                <div className="flex items-center">
                  <i className="ri-file-text-line w-4 h-4 text-gray-400 mr-2"></i>
                  <span className="font-mono text-xs">{vendor.gstin}</span>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <button className="flex-1 bg-primary/10 text-primary py-2 px-3 rounded-lg hover:bg-primary/20 transition-colors text-sm whitespace-nowrap">
                <i className="ri-edit-line mr-1"></i>
                Edit
              </button>
              <button className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-200 transition-colors text-sm whitespace-nowrap">
                <i className="ri-eye-line mr-1"></i>
                View
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Vendor Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-primary">Add New Vendor</h2>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleAddVendor} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                <input
                  type="text"
                  value={newVendor.name}
                  onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newVendor.category}
                  onChange={(e) => setNewVendor({ ...newVendor, category: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select Category</option>
                  <option value="Gold Supplier">Gold Supplier</option>
                  <option value="Diamond Supplier">Diamond Supplier</option>
                  <option value="Silver Supplier">Silver Supplier</option>
                  <option value="Gemstone Supplier">Gemstone Supplier</option>
                  <option value="Tools & Equipment">Tools & Equipment</option>
                  <option value="Packaging">Packaging</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                <input
                  type="tel"
                  value={newVendor.contact}
                  onChange={(e) => setNewVendor({ ...newVendor, contact: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newVendor.email}
                  onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN (Optional)</label>
                <input
                  type="text"
                  value={newVendor.gstin}
                  onChange={(e) => setNewVendor({ ...newVendor, gstin: e.target.value })}
                  placeholder="15 digit GSTIN"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={addVendor.isPending}
                  className="flex-1 bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {addVendor.isPending ? 'Adding...' : 'Add Vendor'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}